import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, ArrowLeft, FileText, User, Building2, Loader2, MessageSquare, Mic } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOutcomeTracking } from "@/hooks/useOutcomeTracking";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import RatingWidget from "@/components/feedback/RatingWidget";
import FeedbackModal from "@/components/feedback/FeedbackModal";
import PlatformTabs from "@/components/output/PlatformTabs";
import { VoiceRecorder } from "@/components/upload/VoiceRecorder";

interface Transformations {
  linkedin_post?: string;
  case_study?: string;
  slide_bullets?: string;
  one_liner?: string;
  carousel_captions?: string[];
  email_template?: string;
  instagram_caption?: string;
  x_thread?: string[];
  x_single?: string;
  raw?: string;
}

const UploadPage = () => {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const [testimonialText, setTestimonialText] = useState("");
  const [clientName, setClientName] = useState("");
  const [brandName, setBrandName] = useState("");
  const [context, setContext] = useState("");
  const [industry, setIndustry] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [transformations, setTransformations] = useState<Transformations | null>(null);
  const [decisionId, setDecisionId] = useState<string | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [inputMode, setInputMode] = useState<"text" | "voice">("text");
  const { trackCopy, trackShare } = useOutcomeTracking();

  useEffect(() => {
    if (!user) {
      navigate("/auth");
    }
  }, [user, navigate]);

  const outputTypes = [
    "linkedin_post",
    "case_study",
    "slide_bullets",
    "one_liner",
    "carousel_captions",
    "email_template",
    "instagram_caption",
    "x_thread",
    "x_single",
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!profile || profile.credits_remaining <= 0) {
      toast.error("You don't have enough credits. Please upgrade your plan.");
      navigate("/pricing");
      return;
    }

    setIsProcessing(true);
    setTransformations(null);

    try {
      // First, save the testimonial to the database
      const { data: testimonialData, error: testimonialError } = await supabase
        .from("testimonials")
        .insert({
          user_id: user!.id,
          client_name: clientName,
          brand_name: brandName,
          context: context || null,
          original_text: testimonialText,
        })
        .select()
        .single();

      if (testimonialError) throw testimonialError;

      // Call the AI transformation edge function
      const { data, error } = await supabase.functions.invoke("transform-testimonial", {
        body: {
          testimonial: testimonialText,
          clientName,
          brandName,
          context,
          industry,
          outputTypes,
        },
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      setTransformations(data.transformations);
      setDecisionId(data.decisionId || null);

      // Save transformations to database
      const transformationInserts = Object.entries(data.transformations)
        .filter(([key]) => key !== "raw")
        .map(([outputType, content]) => ({
          testimonial_id: testimonialData.id,
          user_id: user!.id,
          output_type: outputType,
          content: typeof content === "string" ? content : JSON.stringify(content),
        }));

      if (transformationInserts.length > 0) {
        await supabase.from("transformations").insert(transformationInserts);
      }

      // Atomically deduct credit (prevents race conditions)
      const { error: creditError } = await supabase.rpc("decrement_credit", {
        user_uuid: user!.id,
      });

      if (creditError) {
        console.error("Credit deduction failed:", creditError);
        toast.error("Failed to deduct credit. Your transformation was saved.");
      }

      await refreshProfile();
      toast.success("Testimonial transformed successfully!");
    } catch (error) {
      console.error("Transformation error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to transform testimonial");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVoiceTranscription = (text: string) => {
    setTestimonialText(text);
    setInputMode("text"); // Switch back to text view to show transcription
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-background to-accent/20">
      <Navbar />

      <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {!transformations ? (
          <>
            <div className="text-center mb-8 animate-fade-in">
              <h1 className="font-editorial text-4xl text-foreground mb-4">
                Transform Your Testimonial
              </h1>
              <p className="text-xl text-muted-foreground">
                Paste text or record audio — get platform-ready content in 60 seconds
              </p>
            </div>

            <Card className="border-brand-100 shadow-xl animate-fade-in-up">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Sparkles className="mr-2 h-5 w-5 text-brand-600" />
                  Testimonial Details
                </CardTitle>
                <CardDescription>
                  Provide context for better AI-generated content
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Input Mode Toggle */}
                  <Tabs value={inputMode} onValueChange={(v) => setInputMode(v as "text" | "voice")}>
                    <TabsList className="grid w-full grid-cols-2 max-w-md">
                      <TabsTrigger value="text" className="gap-2">
                        <FileText className="h-4 w-4" />
                        Paste Text
                      </TabsTrigger>
                      <TabsTrigger value="voice" className="gap-2">
                        <Mic className="h-4 w-4" />
                        Record Voice
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="text" className="mt-4">
                      <div className="space-y-2">
                        <Label htmlFor="testimonial" className="text-base font-medium flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          Testimonial Content *
                        </Label>
                        <Textarea
                          id="testimonial"
                          placeholder="Paste the raw testimonial here..."
                          value={testimonialText}
                          onChange={(e) => setTestimonialText(e.target.value)}
                          rows={6}
                          required
                          className="resize-none"
                        />
                      </div>
                    </TabsContent>

                    <TabsContent value="voice" className="mt-4">
                      <div className="space-y-4">
                        <VoiceRecorder onTranscription={handleVoiceTranscription} />
                        {testimonialText && (
                          <div className="space-y-2">
                            <Label className="text-base font-medium">Transcribed Text</Label>
                            <Textarea
                              value={testimonialText}
                              onChange={(e) => setTestimonialText(e.target.value)}
                              rows={6}
                              className="resize-none"
                            />
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="clientName" className="text-base font-medium flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        Client Name *
                      </Label>
                      <Input
                        id="clientName"
                        placeholder="John Smith"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="brandName" className="text-base font-medium flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        Brand/Company Name *
                      </Label>
                      <Input
                        id="brandName"
                        placeholder="Acme Corp"
                        value={brandName}
                        onChange={(e) => setBrandName(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="context" className="text-base font-medium">
                      Project Context
                    </Label>
                    <Textarea
                      id="context"
                      placeholder="Describe the project, service, or product this testimonial is about..."
                      value={context}
                      onChange={(e) => setContext(e.target.value)}
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="industry" className="text-base font-medium">
                      Industry
                    </Label>
                    <Select value={industry} onValueChange={setIndustry}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select industry (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="technology">Technology</SelectItem>
                        <SelectItem value="healthcare">Healthcare</SelectItem>
                        <SelectItem value="finance">Finance</SelectItem>
                        <SelectItem value="education">Education</SelectItem>
                        <SelectItem value="ecommerce">E-commerce</SelectItem>
                        <SelectItem value="consulting">Consulting</SelectItem>
                        <SelectItem value="marketing">Marketing</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="bg-brand-50 p-4 rounded-lg border border-brand-100">
                    <h3 className="font-medium text-foreground mb-2">
                      Your testimonial will be transformed into:
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {[
                        "LinkedIn Post",
                        "Instagram Caption",
                        "X Thread",
                        "Case Study",
                        "Email Template",
                        "Slide Bullets",
                        "Carousel Content",
                        "One-liner Quote",
                      ].map((format) => (
                        <Badge key={format} variant="secondary" className="bg-background text-brand-700">
                          {format}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isProcessing || !testimonialText || !clientName || !brandName}
                    className="w-full bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-700 hover:to-brand-600 text-white text-lg py-6 shadow-brand"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Transforming with AI...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-5 w-5" />
                        Start Extracting
                      </>
                    )}
                  </Button>

                  {!isProcessing && profile && (
                    <p className="text-sm text-center text-muted-foreground">
                      This will use 1 credit • {profile.credits_remaining} credits remaining
                    </p>
                  )}
                </form>
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setTransformations(null);
                    setTestimonialText("");
                    setClientName("");
                    setBrandName("");
                    setContext("");
                    setIndustry("");
                  }}
                  className="mb-2"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Transform Another
                </Button>
                <h1 className="font-editorial text-3xl text-foreground">
                  Your Marketing Assets
                </h1>
                <p className="text-muted-foreground">
                  Generated for {clientName} at {brandName}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFeedbackModal(true)}
                className="flex items-center gap-2"
              >
                <MessageSquare className="h-4 w-4" />
                Give Feedback
              </Button>
            </div>

            {/* Platform-organized Tabs */}
            <PlatformTabs
              content={transformations}
              onCopy={() => trackCopy(decisionId)}
              onShare={() => trackShare(decisionId)}
            />

            {/* Quick Rating */}
            <Card className="mt-6 border-brand-100">
              <CardContent className="pt-6">
                <RatingWidget decisionId={decisionId} variant="stars" />
              </CardContent>
            </Card>

            {/* Feedback Modal */}
            <FeedbackModal
              open={showFeedbackModal}
              onOpenChange={setShowFeedbackModal}
              decisionId={decisionId}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default UploadPage;
